import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { AlertTriangle, X } from "lucide-react";

const LIVE_HOSTS = ["jotbay.ai", "www.jotbay.ai"];
const DISMISS_KEY = "jotbay_env_banner_dismissed";

// Shows a persistent warning on any non-production host (preview / localhost) so nobody
// accidentally treats the preview environment as the live site (e.g. sending real invites,
// whose accounts would then live only in the preview database).
export default function EnvBanner() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === "1");

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isLive = LIVE_HOSTS.includes(host);
  if (isLive || dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] max-w-[92vw]"
      data-testid="env-preview-banner"
    >
      <div className="flex items-center gap-3 rounded-full border border-amber-300 bg-amber-50/95 backdrop-blur px-4 py-2 shadow-lg shadow-amber-900/10">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-xs sm:text-sm text-amber-900 leading-snug">
          <span className="font-semibold">{t("envBanner.label")}</span>{" "}
          <Trans
            i18nKey="envBanner.text"
            components={{
              live: (
                <a
                  href="https://www.jotbay.ai"
                  className="font-semibold underline decoration-amber-400 underline-offset-2 hover:text-amber-950"
                  data-testid="env-banner-live-link"
                >
                  www.jotbay.ai
                </a>
              ),
            }}
          />
        </p>
        <button
          onClick={dismiss}
          aria-label={t("envBanner.dismiss")}
          data-testid="env-banner-dismiss"
          className="ml-1 inline-flex items-center justify-center h-6 w-6 shrink-0 rounded-full text-amber-700 hover:bg-amber-100 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
